import React from 'react';
import { motion } from 'framer-motion';
import { 
  Link, 
  Zap, 
  Shield, 
  Globe, 
  BarChart3, 
  CheckCircle, 
  ArrowRight,
  Settings
} from 'lucide-react';
import Navigation from './Navigation';
import Footer from './Footer';

const IntegrationsPage: React.FC = () => {
  const integrations = [
    {
      category: "E-commerce",
      items: [
        {
          name: "Shopify",
          description: "Seamlessly integrate custom tailoring into your Shopify store",
          logo: "🛍️",
          features: ["Product customization", "Order sync", "Inventory management", "Customer portal"],
          status: "Available"
        },
        {
          name: "WooCommerce",
          description: "Add custom tailoring capabilities to your WordPress store",
          logo: "📦",
          features: ["Plugin integration", "Payment processing", "Order management", "Customer accounts"],
          status: "Available"
        },
        {
          name: "Magento",
          description: "Enterprise e-commerce integration for large-scale operations",
          logo: "🏢",
          features: ["Multi-store support", "Advanced workflows", "B2B features", "Custom extensions"],
          status: "Coming Soon"
        }
      ]
    },
    {
      category: "Payment & Finance",
      items: [
        {
          name: "Razorpay",
          description: "Secure payment processing with Indian payment methods",
          logo: "💳",
          features: ["UPI support", "Card payments", "Installments", "Refund handling"],
          status: "Available"
        },
        {
          name: "Paytm",
          description: "Popular Indian digital wallet and payment gateway",
          logo: "📱",
          features: ["Wallet integration", "QR payments", "Bank transfers", "Bill payments"],
          status: "Available"
        },
        {
          name: "Stripe",
          description: "Global payment processing for international customers",
          logo: "🌍",
          features: ["Multi-currency", "International cards", "Subscription billing", "Advanced fraud protection"],
          status: "Available"
        }
      ]
    },
    {
      category: "Communication & CRM",
      items: [
        {
          name: "WhatsApp Business",
          description: "Direct customer communication through WhatsApp",
          logo: "💬",
          features: ["Order updates", "Measurement sharing", "Customer support", "Automated responses"],
          status: "Available"
        },
        {
          name: "Zoho CRM",
          description: "Customer relationship management for tailors",
          logo: "👥",
          features: ["Lead management", "Customer profiles", "Sales tracking", "Reporting"],
          status: "Available"
        },
        {
          name: "Freshdesk",
          description: "Customer support and ticketing system",
          logo: "🎫",
          features: ["Support tickets", "Knowledge base", "Live chat", "Customer satisfaction"],
          status: "Coming Soon"
        }
      ]
    },
    {
      category: "Design & CAD",
      items: [
        {
          name: "Adobe Creative Suite",
          description: "Professional design tools for fashion designers",
          logo: "🎨",
          features: ["Pattern design", "Fabric visualization", "3D modeling", "Color management"],
          status: "Available"
        },
        {
          name: "Optitex",
          description: "Advanced pattern making and 3D garment simulation",
          logo: "✂️",
          features: ["Pattern creation", "3D fitting", "Grading", "Production planning"],
          status: "Coming Soon"
        },
        {
          name: "Browzwear",
          description: "3D fashion design and virtual prototyping",
          logo: "👗",
          features: ["Virtual fitting", "Fabric simulation", "Design collaboration", "Sample reduction"],
          status: "Coming Soon"
        }
      ]
    },
    {
      category: "Logistics & Shipping",
      items: [
        {
          name: "Delhivery",
          description: "Reliable logistics and delivery services across India",
          logo: "🚚",
          features: ["Express delivery", "COD support", "Real-time tracking", "Returns management"],
          status: "Available"
        },
        {
          name: "Shiprocket",
          description: "Multi-courier shipping platform for e-commerce",
          logo: "📦",
          features: ["Multi-courier", "Automated shipping", "Tracking integration", "Shipping calculator"],
          status: "Available"
        },
        {
          name: "DHL",
          description: "International shipping and logistics services",
          logo: "🌐",
          features: ["Global reach", "Express shipping", "Customs clearance", "Package insurance"],
          status: "Available"
        }
      ]
    },
    {
      category: "Analytics & Business Intelligence",
      items: [
        {
          name: "Google Analytics",
          description: "Website and business performance analytics",
          logo: "📊",
          features: ["Traffic analysis", "Conversion tracking", "Customer behavior", "Performance insights"],
          status: "Available"
        },
        {
          name: "Tableau",
          description: "Advanced data visualization and business intelligence",
          logo: "📈",
          features: ["Interactive dashboards", "Data exploration", "Real-time insights", "Custom reports"],
          status: "Coming Soon"
        },
        {
          name: "Power BI",
          description: "Microsoft's business analytics platform",
          logo: "🔍",
          features: ["Data modeling", "Visual reports", "AI insights", "Collaboration tools"],
          status: "Coming Soon"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <Zap className="w-8 h-8 text-brand-blue" />,
      title: "Seamless Integration",
      description: "Connect your existing tools and workflows without disruption"
    },
    {
      icon: <Shield className="w-8 h-8 text-brand-blue" />,
      title: "Secure & Reliable",
      description: "Enterprise-grade security and 99.9% uptime guarantee"
    },
    {
      icon: <Globe className="w-8 h-8 text-brand-blue" />,
      title: "Global Reach",
      description: "Access customers and partners worldwide with localized features"
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-brand-blue" />,
      title: "Data Insights",
      description: "Unified analytics across all your integrated platforms"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-brand-white to-brand-blue/10">
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-brand-blue rounded-full shadow-lg">
                <Link className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Master Ji <span className="text-brand-blue">Integrations</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connect with your favorite tools and platforms to streamline your tailoring business
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why <span className="text-brand-blue">Integrate</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Powerful integrations that transform how you run your tailoring business
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="flex items-center justify-center w-16 h-16 bg-brand-blue/10 rounded-xl mb-4 mx-auto">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Available <span className="text-brand-blue">Integrations</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Browse our comprehensive collection of integrations organized by category
            </p>
          </motion.div>

          <div className="space-y-16">
            {integrations.map((category, categoryIndex) => (
              <motion.div
                key={categoryIndex}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                  {category.category}
                </h3>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.items.map((integration, integrationIndex) => (
                    <motion.div
                      key={integrationIndex}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: integrationIndex * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="text-3xl">{integration.logo}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                              {integration.name}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              integration.status === 'Available' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {integration.status}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {integration.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <h5 className="font-medium text-gray-800 text-sm">Key Features:</h5>
                        <ul className="space-y-1">
                          {integration.features.map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-center space-x-2">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span className="text-xs text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <button className="w-full bg-brand-blue text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-brand-blue/90 transition-colors">
                        {integration.status === 'Available' ? 'Connect Now' : 'Get Notified'}
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Integration Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-gradient-to-r from-brand-blue to-brand-yellow rounded-full">
                <Settings className="w-12 h-12 text-white" />
              </div>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Need a <span className="text-brand-blue">Custom Integration</span>?
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              We can build custom integrations for your specific business needs and existing systems
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-2">Custom Development</h4>
                <p className="text-gray-600 text-sm">
                  Tailored integrations built specifically for your workflow and requirements
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-2">API Access</h4>
                <p className="text-gray-600 text-sm">
                  Full API access to build your own integrations and custom solutions
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium flex items-center justify-center space-x-2">
                <span>Request Custom Integration</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors font-medium">
                Contact Sales
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-blue/5 to-brand-yellow/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Ready to <span className="text-brand-blue">Connect</span>?
            </h2>
            
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Start integrating your favorite tools and transform your tailoring business today
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-brand-blue text-white rounded-lg hover:bg-brand-blue/90 transition-colors font-medium flex items-center justify-center space-x-2">
                <span>Explore Integrations</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="px-8 py-4 border border-brand-blue text-brand-blue rounded-lg hover:bg-brand-blue/10 transition-colors font-medium">
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IntegrationsPage;

